import { QueryError } from '@graphql-box/helpers';

import { useQuery } from '@graphql-box/react';
import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { useEffect } from 'react';
import { isFiltersDataStale } from '#components/Filters/helpers/isFiltersDataStale.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { Loader } from '../Loader/index.tsx';
import { Component as DefaultComponent } from './Component.tsx';
import { areDiscoverMovieFilters } from './helpers/areDiscoverFilters.ts';
import { formatFilterOptions } from './helpers/formatFilterOptions.ts';
import { transformMovieFiltersToValues, transformTvFiltersToValues } from './helpers/transformFiltersToValues.ts';
import { type FiltersResponse, type QueryProps } from './types.ts';

const queries = {
  [ScreenType.Movie]: "query GetMovieFilters(\n  $screenType: ScreenType!\n) {\n  certifications(screenType: $screenType) {\n    AU {\n      certification\n      meaning\n      order\n    }\n    BR {\n      certification\n      meaning\n      order\n    }\n    CA {\n      certification\n      meaning\n      order\n    }\n    CA_QC {\n      certification\n      meaning\n      order\n    }\n    DE {\n      certification\n      meaning\n      order\n    }\n    FR {\n      certification\n      meaning\n      order\n    }\n    GB {\n      certification\n      meaning\n      order\n    }\n    IN {\n      certification\n      meaning\n      order\n    }\n    KR {\n      certification\n      meaning\n      order\n    }\n    NL {\n      certification\n      meaning\n      order\n    }\n    NZ {\n      certification\n      meaning\n      order\n    }\n    PT {\n      certification\n      meaning\n      order\n    }\n    RU {\n      certification\n      meaning\n      order\n    }\n    SK {\n      certification\n      meaning\n      order\n    }\n    TH {\n      certification\n      meaning\n      order\n    }\n    US {\n      certification\n      meaning\n      order\n    }\n  }\n  genres(screenType: $screenType) {\n    name\n  }\n}\n",
  [ScreenType.Tv]: "query GetTvFilters(\n  $screenType: ScreenType!\n) {\n  genres(screenType: $screenType) {\n    name\n  }\n}\n",
};

export const FiltersQuery = ({
  Component = DefaultComponent,
  addToShortcuts,
  children,
  enableLoader,
  filtersParam,
  setFiltersParam,
  setOpenFiltersDrawer,
  type,
}: QueryProps) => {
  const { config } = useToolbelt();
  const query = queries[type];

  const { execute, loading, result } = useQuery<FiltersResponse>(query, {
    loading: true,
  });

  useEffect(() => {
    void execute({ variables: { screenType: type } }, { data: { initiator: 'Filters' } });
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  if (result instanceof QueryError) {
    console.error(result);
    return;
  }

  if (result?.errors?.length) {
    console.error(result.errors);
  }

  if (enableLoader && (loading || isFiltersDataStale(result?.data, type))) {
    return <Loader sx={{ marginTop: theme => theme.spacing(5) }} />;
  }

  const filterOptions = formatFilterOptions(result?.data, config);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!filterOptions) {
    return;
  }

  const initialValues = areDiscoverMovieFilters(filtersParam, type)
    ? transformMovieFiltersToValues(filtersParam, filterOptions)
    : transformTvFiltersToValues(filtersParam, filterOptions);

  const component = (
    <Component
      addToShortcuts={addToShortcuts}
      filterOptions={filterOptions}
      filtersParam={filtersParam}
      initialValues={initialValues}
      setFiltersParam={setFiltersParam}
      setOpenFiltersDrawer={setOpenFiltersDrawer ?? (() => true)}
      type={type}
    />
  );

  return children ? children(component) : component;
};
